import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Snackbar, Tab, Tabs } from '@mui/material';

import { usePermissions } from '@/shared/permissions';
import { PageContainer } from '@/shared/ui';

import { ChannelsPanel } from '../components/ChannelsPanel';
import { GroupsPanel } from '../components/GroupsPanel';
import { OverridesPanel } from '../components/OverridesPanel';
import { StoresPanel } from '../components/StoresPanel';

type TabKey = 'groups' | 'channels' | 'stores' | 'overrides';
const TAB_KEYS: TabKey[] = ['groups', 'channels', 'stores', 'overrides'];

interface ToastState {
  message: string;
  severity: 'success' | 'error' | 'warning';
}

/**
 * "Sales channels -> Stock" — the channel-availability configuration page
 * (plan-platform-001 WP2.3b, built exactly from design-stock-channels-page): location groups →
 * channel scopes → store scopes (allocation mode + buffer + MIRROR warning) → audited overrides,
 * with the derivation drill-down making every per-store number explainable. Tabs are in-page state
 * synced to `?tab=` / `?store=` (deep links from the vendor Retail panel and store rows).
 */
export function ChannelStockPage() {
  const { t } = useTranslation('channelStock');
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('inventory.manage');
  const canAdmin = hasPermission('settings.inventory.manage');

  const [searchParams, setSearchParams] = useSearchParams();
  const paramTab = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey>(paramTab && TAB_KEYS.includes(paramTab) ? paramTab : 'groups');
  const [seedStore, setSeedStore] = useState<string | null>(searchParams.get('store'));

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (seedStore) next.set('store', seedStore);
    else next.delete('store');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, seedStore]);

  const [toast, setToast] = useState<ToastState | null>(null);
  const notify = useCallback(
    (message: string, severity: ToastState['severity'] = 'success') => setToast({ message, severity }),
    [],
  );

  const openOverridesFor = useCallback((storeCode: string) => {
    setSeedStore(storeCode);
    setTab('overrides');
  }, []);

  return (
    <PageContainer title={t('page.title')} subtitle={t('page.subtitle')}>
      {!canManage ? (
        <Alert severity="info" sx={{ mb: 2 }}>{t('page.readOnlyNotice')}</Alert>
      ) : null}
      <Tabs
        value={tab}
        onChange={(_event, value: TabKey) => setTab(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab value="groups" label={t('tabs.groups')} />
        <Tab value="channels" label={t('tabs.channels')} />
        <Tab value="stores" label={t('tabs.stores')} />
        <Tab value="overrides" label={t('tabs.overrides')} />
      </Tabs>

      {tab === 'groups' ? <GroupsPanel canManage={canManage} onToast={notify} /> : null}
      {tab === 'channels' ? <ChannelsPanel canManage={canManage} onToast={notify} /> : null}
      {tab === 'stores' ? (
        <StoresPanel canManage={canManage} onToast={notify} onOpenOverrides={openOverridesFor} />
      ) : null}
      {tab === 'overrides' ? (
        <OverridesPanel canAdmin={canAdmin} seedStore={seedStore} onToast={notify} />
      ) : null}

      <Snackbar
        open={toast !== null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
